/**
 * Add money to the organization wallet (UPI / card through the platform's
 * Razorpay checkout, purpose `org_wallet_topup`; GST is added at payment).
 * Used wherever a Get-it-built payment can't be covered — placing an order,
 * a stage that is payment-due. Pass `suggestMinor` = the shortfall.
 */
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { topupOrgWallet } from '@/api/billing'
import { money } from '@/api/marketplace'
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout'

export default function WalletTopUp({ currency, suggestMinor, onPaid, compact = false }: {
  currency: string
  suggestMinor: number
  onPaid: () => void
  compact?: boolean
}) {
  const { checkout, busy } = useRazorpayCheckout()
  const [amount, setAmount] = useState(Math.max(suggestMinor, 100) / 100)
  useEffect(() => setAmount(Math.ceil(Math.max(suggestMinor, 100) / 100)), [suggestMinor])
  const pay = async () => {
    const minor = Math.round(amount * 100)
    if (minor <= 0) return
    try {
      const r = await checkout({ purpose: 'org_wallet_topup', description: 'Organization wallet top-up', precreate: () => topupOrgWallet(minor, currency) })
      if (r.cancelled) return
      if (!r.ok) { toast.error(r.error || 'The payment did not go through.'); return }
      toast.success('Money added to your organization wallet')
      onPaid()
    } catch (e) {
      toast.error('The payment could not be started. Please try again.')
    }
  }
  return (
    <div className="mkt-col" style={{ gap: 8 }}>
      {!compact && <span className="mkt-muted" style={{ fontSize: 14 }}>Short by <strong style={{ color: 'var(--color-text)' }}>{money(suggestMinor, currency)}</strong>. Add money to your organization wallet:</span>}
      <div className="mkt-row" style={{ gap: 8 }}>
        <div className="mkt-money"><span>{currency === 'INR' ? '₹' : '$'}</span>
          <input aria-label="Amount to add" inputMode="decimal" value={amount} onChange={(e) => setAmount(Number(e.target.value.replace(/[^\d.]/g, '')) || 0)} />
        </div>
        <button type="button" className="btn btn-primary" onClick={pay} disabled={busy || amount <= 0}>{busy ? 'Opening payment…' : 'Add with UPI or card'}</button>
      </div>
      <span className="mkt-faint">GST is added at payment.</span>
    </div>
  )
}
